import { ComponentName, Config, ModalPriority } from '@constants';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { sortAscending } from '@utils/index.ts';
import { changeBodyScroll } from '@utils/uiUtils.ts';
import { setLogin } from './user';

export type ModalPayload = { name: ComponentName; data?: any };
export type ModalsState = ModalPayload[];

export const getModalName = (
  payload: ModalPayload | ComponentName,
): ComponentName => {
  if (typeof payload === 'object' && payload !== null && 'name' in payload) {
    return (payload as { name: ComponentName }).name;
  }
  return payload;
};

export const getModalData = (payload: ModalPayload | ComponentName) =>
  typeof payload === 'object' ? { ...payload.data } : {};

const initialState: ModalsState = [];

const sortModalsPriority = (modals: ModalsState) =>
  modals.sort((a, b) => {
    const aPriority = ModalPriority[a.name] || 100;
    const bPriority = ModalPriority[b.name] || 100;
    return sortAscending(aPriority, bPriority);
  });

export const modalsSlice = createSlice({
  name: 'modals',
  initialState,
  reducers: {
    enableModal: (
      state,
      action: PayloadAction<ModalPayload | ComponentName>,
    ) => {
      const modalName = getModalName(action.payload);
      if (
        action.payload &&
        !state.map(modal => getModalName(modal)).includes(modalName)
      ) {
        changeBodyScroll(false);
        return sortModalsPriority([
          ...state,
          { name: modalName, data: getModalData(action.payload) },
        ]);
      }
    },
    disableModal: (
      state,
      action: PayloadAction<ModalPayload | ComponentName>,
    ) => {
      const modalName = getModalName(action.payload);
      const modalNames = state.map(modal => getModalName(modal));
      if (action.payload && modalNames.includes(modalName)) {
        const elIndex = modalNames.indexOf(modalName);
        state.splice(elIndex, 1);
        if (!state.length) {
          changeBodyScroll(true);
        }
        return state;
      }
    },
  },
  extraReducers: {
    [setLogin.toString()]: state => {
      if (Config.componentSettings?.modals.ResponsibleGambling) {
        return sortModalsPriority([
          ...(state || []),
          { name: ComponentName.ResponsibleGamblingModal },
        ]);
      }
    },
  },
});

export const { enableModal, disableModal } = modalsSlice.actions;

export default modalsSlice.reducer;
